import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { AdminLayout } from '@/components/layouts/admin-layout';
import { Megaphone, Send, Clock, Users, AlertCircle, CheckCircle2, History } from 'lucide-react';

interface Broadcast {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  data: string;
  read: boolean;
  createdAt: string;
  readAt?: string;
}

interface BroadcastHistoryResponse {
  broadcasts: Broadcast[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
}

export default function AdminBroadcastsPage() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState<'all' | 'active' | 'followers'>('all');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [isSending, setIsSending] = useState(false);
  const [history, setHistory] = useState<Broadcast[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [lastSentCount, setLastSentCount] = useState<number | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const response = await api.get<BroadcastHistoryResponse>('api/admin/broadcast/history?limit=20');
      if (response.success && response.data) {
        setHistory(response.data.broadcasts || []);
      }
    } catch (error) {
      console.error('Failed to fetch broadcast history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSendBroadcast = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    setShowConfirmation(true);
  };

  const confirmSendBroadcast = async () => {
    setIsSending(true);
    setShowConfirmation(false);

    try {
      const response = await api.post<{ deliveredCount: number }>('api/admin/broadcast', {
        title: title.trim(),
        message: message.trim(),
        targetAudience,
        priority,
      });

      if (response.success) {
        const count = response.data?.deliveredCount || 0;
        setLastSentCount(count);
        toast.success(`Broadcast sent to ${count} users`);
        setTitle('');
        setMessage('');
        setTargetAudience('all');
        setPriority('normal');
        fetchHistory();
      } else {
        toast.error(response.error?.message || 'Failed to send broadcast');
      }
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      toast.error('Failed to send broadcast');
    } finally {
      setIsSending(false);
    }
  };

  const getPriorityColor = (p: string) => {
    switch (p) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Broadcasts</h1>
          <p className="text-muted-foreground mt-1">
            Send system-wide announcements to users
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Create Broadcast Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5" />
                New Broadcast
              </CardTitle>
              <CardDescription>
                Create and send a system announcement to users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Enter broadcast title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="Enter your announcement message"
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Target Audience</Label>
                  <Select value={targetAudience} onValueChange={(v) => setTargetAudience(v as typeof targetAudience)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="active">Active Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={priority} onValueChange={(v) => setPriority(v as typeof priority)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {lastSentCount !== null && (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Last broadcast delivered to {lastSentCount} users</span>
                </div>
              )}

              <Button
                className="w-full"
                onClick={handleSendBroadcast}
                disabled={!title.trim() || !message.trim() || isSending}
              >
                {isSending ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Broadcast
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 bg-muted/30">
                {title || message ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getPriorityColor(priority)}>
                        {priority}
                      </Badge>
                      <span className="text-xs text-muted-foreground">System Announcement</span>
                    </div>
                    <h3 className="font-semibold">{title || 'Untitled'}</h3>
                    <p className="text-sm text-muted-foreground">{message || 'No message'}</p>
                    <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>Just now</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Fill in the form to see a preview
                  </p>
                )}
              </div>

              <Separator className="my-4" />

              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Delivery Summary
                </h4>
                <div className="text-sm text-muted-foreground">
                  <p>Target: <span className="font-medium text-foreground capitalize">{targetAudience}</span></p>
                  <p>Priority: <span className="font-medium text-foreground capitalize">{priority}</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Broadcast History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Broadcast History
            </CardTitle>
            <CardDescription>
              View past system announcements
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingHistory ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No broadcasts sent yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((broadcast) => (
                  <div
                    key={broadcast.id}
                    className="flex items-start justify-between p-4 border rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={getPriorityColor(JSON.parse(broadcast.data || '{}').priority || 'normal')}>
                          {JSON.parse(broadcast.data || '{}').priority || 'normal'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(broadcast.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <h4 className="font-medium">{broadcast.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {broadcast.message}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-md w-full mx-4">
              <CardHeader>
                <CardTitle>Confirm Broadcast</CardTitle>
                <CardDescription>
                  You are about to send a broadcast to {targetAudience === 'all' ? 'all users' : targetAudience === 'active' ? 'active users' : 'followers'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="font-medium">{title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{message}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span>This action cannot be undone</span>
                </div>
              </CardContent>
              <div className="flex gap-2 p-4 pt-0">
                <Button variant="outline" onClick={() => setShowConfirmation(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={confirmSendBroadcast} className="flex-1">
                  Confirm & Send
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
